import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { customerApi } from "../Apis/Api";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { formatDateTime } from "../utils/formatDateTime";
import PaginationControls from "../components/ui/PaginationControls";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { TextWrap } from "../utils/textWrap";
import { AddButton } from "../components/ui/Button";
import { handleApiResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal";

const CustomerProfilePage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const fieldMapping = {
    customer_name: "customer_name",
    email: "email",
    mobile_number: "mobile_number",
    national_id: "national_id",
    loyalty_code: "loyalty_code",
    plate_number: "plate_number",
    vehicle_color: "vehicle_color",
    country_name: "country_name",
    authority_name: "authority_name",
    type_name: "type_name",
    make_name: "make_name",
    model_name: "model_name",
    is_blacklisted: "is_blacklisted",
    created_datetime: "created_datetime",
    created_by: "created_by",
  };

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(sortConfig.field && {
          sort_by: fieldMapping[sortConfig.field] || sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };
      const response = await customerApi.getCustomerProfiles(params);
      let data = (response.data || []).map((customer, index) => ({
        ...customer,
        id: customer.customer_id || index + 1,
        display_created_datetime: customer.created_datetime
          ? formatDateTime(customer.created_datetime)
          : "-",
        is_blacklisted:
          customer.is_blacklisted !== undefined &&
          customer.is_blacklisted !== null
            ? customer.is_blacklisted
              ? "Inactive"
              : "Active"
            : "-",
      }));

      if (sortConfig.field) {
        data = [...data].sort((a, b) => {
          const valueA = a[sortConfig.field] || "";
          const valueB = b[sortConfig.field] || "";
          if (sortConfig.field === "created_datetime") {
            const dateA = new Date(a.created_datetime || 0);
            const dateB = new Date(b.created_datetime || 0);
            return sortConfig.direction === "asc"
              ? dateA - dateB
              : dateB - dateA;
          }
          return sortConfig.direction === "asc"
            ? valueA.toString().localeCompare(valueB.toString(), undefined, {
                sensitivity: "base",
              })
            : valueB.toString().localeCompare(valueA.toString(), undefined, {
                sensitivity: "base",
              });
        });
      }

      setCustomers(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || data.length,
      }));
      setError(null);
      handleApiResponse(response, "fetch customers", "GET"); // ✅ FIXED
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch customers. Please check the API endpoint.";
      setError(errorMessage);
      setCustomers([]);
      handleApiResponse(err, "Failed to fetch customers", "GET"); // ✅ FIXED
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    setLoading(true);
    try {
      const response = await customerApi.deleteCustomer(
        customerToDelete.customer_id
      );

      handleApiResponse(
        response,
        `Customer "${customerToDelete.customer_name}" deleted successfully`,
        "delete customer",
        "DELETE"
      ); // ✅ ADDED
      setCustomers((prev) =>
        prev.filter(
          (customer) => customer.customer_id !== customerToDelete.customer_id
        )
      );
      setPagination((prev) => ({
        ...prev,
        total: prev.total - 1,
        currentPage: Math.min(
          prev.currentPage,
          Math.ceil((prev.total - 1) / prev.perPage) || 1
        ),
      }));
      setShowDeleteModal(false);
      setCustomerToDelete(null);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete customer. Please try again.";
      setError(errorMessage);
      // toast.error(errorMessage);
      handleApiResponse(err, "Failed to delete customer", "DELETE"); // ✅ ADDED
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setCustomerToDelete(null);
    }
  };

  const openDeleteModal = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCustomerToDelete(null);
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  useEffect(() => {
    fetchCustomers();
  }, [pagination.currentPage, pagination.perPage, debouncedSearch, sortConfig]);

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchCustomers} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search By Name, Email, National Id or Loyalty Code"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search customers"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2">
            {" "}
            {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Customer List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add New Customer"
              permissionKey="add_customer_profile"
              onClick={() => navigate("/customer-profile-create")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Customer List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {customers.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No customers match your search or filter."
                  : "No customer data available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0 table-hover">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("customer_name")}
                    role="button"
                    aria-label={`Sort by customer name, currently sorted ${
                      sortConfig.field === "customer_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Name
                      <SortIcons
                        field="customer_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("mobile_number")}
                    role="button"
                    aria-label={`Sort by phone, currently sorted ${
                      sortConfig.field === "mobile_number"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Phone
                      <SortIcons
                        field="mobile_number"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("loyalty_code")}
                    role="button"
                    aria-label={`Sort by loyalty_code, currently sorted ${
                      sortConfig.field === "loyalty_code"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Loyalty Code
                      <SortIcons field="loyalty_code" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("plate_number")}
                    role="button"
                    aria-label={`Sort by plate number, currently sorted ${
                      sortConfig.field === "plate_number"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Plate Number
                      <SortIcons field="plate_number" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("vehicle_color")}
                    role="button"
                    aria-label={`Sort by vehicle color, currently sorted ${
                      sortConfig.field === "vehicle_color"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Vehicle Color
                      <SortIcons
                        field="vehicle_color"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("country_name")}
                    role="button"
                    aria-label={`Sort by country name, currently sorted ${
                      sortConfig.field === "country_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Registered Country
                      <SortIcons field="country_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("authority_name")}
                    role="button"
                    aria-label={`Sort by authority name, currently sorted ${
                      sortConfig.field === "authority_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Authority
                      <SortIcons
                        field="authority_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("type_name")}
                    role="button"
                    aria-label={`Sort by type name, currently sorted ${
                      sortConfig.field === "type_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Vehicle Type
                      <SortIcons field="type_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("make_name")}
                    role="button"
                    aria-label={`Sort by make name, currently sorted ${
                      sortConfig.field === "make_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Make
                      <SortIcons field="make_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("model_name")}
                    role="button"
                    aria-label={`Sort by model name, currently sorted ${
                      sortConfig.field === "model_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Model
                      <SortIcons field="model_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("email")}
                    role="button"
                    aria-label={`Sort by email, currently sorted ${
                      sortConfig.field === "email"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Email
                      <SortIcons field="email" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("national_id")}
                    role="button"
                    aria-label={`Sort by national_id, currently sorted ${
                      sortConfig.field === "national_id"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      National ID
                      <SortIcons field="national_id" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_blacklisted")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_blacklisted"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      Status
                      <SortIcons
                        field="is_blacklisted"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_datetime")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_datetime"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="created_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <tr key={customer.customer_id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.customer_name || ""}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.mobile_number || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.loyalty_code || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.plate_number || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.vehicle_color || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.country_name || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.authority_name || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.type_name || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.make_name || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.model_name || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.email || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.national_id || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.created_by || "-"}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={customer.is_blacklisted} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {customer.display_created_datetime}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("update_customer_profile") && (
                          <button
                            type="button"
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() =>
                              navigate("/customer-profile-edit", {
                                state: { customer },
                              })
                            }
                            title={`Edit ${customer.customer_name}`}
                            aria-label={`Edit customer ${customer.customer_name}`}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {hasPermission("delete_customer_profile") && (
                          <button
                            type="button"
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => openDeleteModal(customer)}
                            title={`Delete ${customer.customer_name}`}
                            aria-label={`Delete customer ${customer.customer_name}`}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Delete Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none",backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeDeleteModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>
                  {customerToDelete?.customer_name || "this customer"}
                </strong>
                ?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger d-flex align-items-center"
                onClick={handleDeleteCustomer}
                aria-label={`Confirm delete customer ${
                  customerToDelete?.customer_name || ""
                }`}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}
     <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Customer Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This page is your <strong>central dashboard</strong> for managing all <strong>Customers</strong>. 
    You can view complete customer details, add new customers, and edit or delete existing ones.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">What You See in the List</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">Customer Name</li>
    <li className="list-group-item">Phone Number</li>
    <li className="list-group-item">Loyalty Code</li>
    <li className="list-group-item">Plate Number</li>
    <li className="list-group-item">Vehicle Color</li>
    <li className="list-group-item">Registered Country</li>
    <li className="list-group-item">Authority</li>
    <li className="list-group-item">Value Type</li>
    <li className="list-group-item">Make</li>
    <li className="list-group-item">Model</li>
    <li className="list-group-item">Email</li>
    <li className="list-group-item">National ID</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Add New Customer – Available Fields</h6>
  <p className="small text-neutral-600 mb-2">When you click "Add Customer", fill in these details:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Name</strong> – Full name of the customer</li>
    <li className="list-group-item"><strong>Phone</strong> – Contact number</li>
    <li className="list-group-item"><strong>Loyalty Code</strong> – Unique loyalty ID (auto or manual)</li>
    <li className="list-group-item"><strong>Plate Number</strong> – Vehicle registration plate</li>
    <li className="list-group-item"><strong>Vehicle Color</strong> – Color of the vehicle</li>
    <li className="list-group-item"><strong>Registered Country</strong> – Country of registration</li>
    <li className="list-group-item"><strong>Authority</strong> – Issuing authority</li>
    <li className="list-group-item"><strong>Value Type</strong> – Type of value (e.g., Standard, VIP)</li>
    <li className="list-group-item"><strong>Make</strong> – Vehicle brand (e.g., Toyota)</li>
    <li className="list-group-item"><strong>Model</strong> – Vehicle model</li>
    <li className="list-group-item"><strong>Email</strong> – Customer email (optional)</li>
    <li className="list-group-item"><strong>National ID</strong> – ID card number</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Two Action Buttons (Per Customer)</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Edit (Green Pencil)</strong><br/>
      Update any customer detail anytime
    </li>
    <li className="list-group-item">
      <strong>Delete (Red Trash)</strong><br/>
      Permanently remove the customer (only if no transactions are linked)
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Note:</strong><br/>
    • Search and filter options help you find customers quickly<br/>
    • Adding a customer is instant – no approval needed<br/>
    • Deleted customers cannot be recovered, so double-check before deleting
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Keep customer data updated → Add new customers right after registration → Better loyalty experience!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Accurate customer records mean smoother operations, happier customers, and stronger loyalty programs.
  </p>
</InfoModal>
    </>
  );
};

export default CustomerProfilePage;
